// The work schedule blob is owned by the device firmware and has no published shape yet.
// This parser accepts the layouts we expect and reports anything it cannot read instead of
// throwing, so an unfamiliar payload degrades to a "cannot display" notice in the UI.
//
// Accepted layouts (all keys case-insensitive):
//   { entries: [ { weekdays, startMinutes, endMinutes } ] }   firmware payload seen in production
//   { schedules: [ { name, days, start, end } ] }              list of named schedules
//   [ { name, days, start, end } ]                              bare list
//   { "<name>": { days, start, end } }                          map keyed by schedule name
//   { monday: [ { start, end } ], tuesday: ... }                map keyed by weekday
// days: array of weekday names ("mon", "monday", "montag") or numbers (ISO 1=Mon..7=Sun, or
// JS 0=Sun..6=Sat when a 0 is present). Times: "HH:MM", hours (<= 24) or minutes since midnight;
// keys ending in "minutes" are always read as minutes.

export interface WorkScheduleEntry {
  id: string;
  // Display name from the payload; undefined when the entry carries none (UI shows a numbered label).
  name?: string;
  // 0 = Monday ... 6 = Sunday
  days: number[];
  startMinutes: number;
  endMinutes: number;
}

export interface ParsedWorkSchedule {
  schedules: WorkScheduleEntry[];
  // true when at least one part of the blob was skipped because its shape was unknown
  hasUnrecognizedParts: boolean;
}

const MAX_WORK_SCHEDULES = 4;
export const MINUTES_PER_DAY = 24 * 60;

const WEEKDAY_ALIASES: Record<string, number> = {
  mon: 0, monday: 0, montag: 0, mo: 0,
  tue: 1, tuesday: 1, dienstag: 1, di: 1,
  wed: 2, wednesday: 2, mittwoch: 2, mi: 2,
  thu: 3, thursday: 3, donnerstag: 3, do: 3,
  fri: 4, friday: 4, freitag: 4, fr: 4,
  sat: 5, saturday: 5, samstag: 5, sa: 5,
  sun: 6, sunday: 6, sonntag: 6, so: 6,
};
const WEEKDAY_NAMES = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const NAME_KEYS = ['name', 'label', 'title', 'id'];
const DAY_KEYS = ['days', 'weekdays', 'dayOfWeek', 'daysOfWeek'];
const START_KEYS = ['startMinutes', 'start', 'from', 'startTime', 'begin'];
const END_KEYS = ['endMinutes', 'end', 'to', 'endTime', 'finish'];

// parseWorkSchedule normalises the opaque schedule blob into at most MAX_WORK_SCHEDULES entries.
export function parseWorkSchedule(blob: unknown): ParsedWorkSchedule {
  if (blob === null || blob === undefined) return { schedules: [], hasUnrecognizedParts: false };

  const candidates = collectScheduleCandidates(blob);
  if (candidates === null) return { schedules: [], hasUnrecognizedParts: true };

  const schedules: WorkScheduleEntry[] = [];
  let hasUnrecognizedParts = false;
  candidates.forEach((candidate, index) => {
    const entry = parseScheduleEntry(candidate.value, candidate.keyName, index);
    if (entry) schedules.push(entry);
    else hasUnrecognizedParts = true;
  });

  const hasTooMany = schedules.length > MAX_WORK_SCHEDULES;
  return {
    schedules: schedules.slice(0, MAX_WORK_SCHEDULES),
    hasUnrecognizedParts: hasUnrecognizedParts || hasTooMany,
  };
}

interface ScheduleCandidate {
  value: unknown;
  // Name taken from the map key when the blob is keyed by schedule name
  keyName?: string;
}

// collectScheduleCandidates finds the list of schedule-like objects in any accepted layout.
// Returns null when the blob is not an object or array at all.
function collectScheduleCandidates(blob: unknown): ScheduleCandidate[] | null {
  if (Array.isArray(blob)) {
    return blob.map((value) => ({ value }));
  }
  if (!isRecord(blob)) return null;

  const listUnderKey = findKey(blob, ['schedules', 'entries', 'items', 'shifts']);
  if (listUnderKey !== undefined && Array.isArray(blob[listUnderKey])) {
    return collectScheduleCandidates(blob[listUnderKey]);
  }

  const entries = Object.entries(blob);
  const isWeekdayKeyed = entries.length > 0 && entries.every(([key]) => weekdayIndexFromName(key) !== undefined);
  if (isWeekdayKeyed) return candidatesFromWeekdayMap(blob);

  // Map keyed by schedule name: every value must be an object to count as a schedule
  return entries.map(([key, value]) => ({ value, keyName: key }));
}

// candidatesFromWeekdayMap turns { monday: [{start,end}], ... } into one candidate per time
// window, merging identical windows across days into a single schedule.
function candidatesFromWeekdayMap(blob: Record<string, unknown>): ScheduleCandidate[] {
  const windows = new Map<string, { start: unknown; end: unknown; days: number[] }>();
  Object.entries(blob).forEach(([dayName, value]) => {
    const dayIndex = weekdayIndexFromName(dayName);
    const dayWindows = Array.isArray(value) ? value : [value];
    dayWindows.forEach((window) => {
      if (!isRecord(window) || dayIndex === undefined) return;
      const start = valueForKeys(window, START_KEYS);
      const end = valueForKeys(window, END_KEYS);
      const windowKey = `${String(start)}-${String(end)}`;
      const existing = windows.get(windowKey) ?? { start, end, days: [] };
      existing.days.push(dayIndex);
      windows.set(windowKey, existing);
    });
  });
  // Days are re-emitted as names so parseDays never mistakes the Monday-based index for JS numbering
  return Array.from(windows.values()).map((window) => ({
    value: { start: window.start, end: window.end, days: window.days.map((day) => WEEKDAY_NAMES[day]) },
  }));
}

// parseScheduleEntry reads one schedule object; returns null if days or times are unreadable.
function parseScheduleEntry(value: unknown, keyName: string | undefined, index: number): WorkScheduleEntry | null {
  if (!isRecord(value)) return null;

  const rawName = valueForKeys(value, NAME_KEYS);
  const name = typeof rawName === 'string' && rawName.trim() ? rawName : keyName;
  const days = parseDays(valueForKeys(value, DAY_KEYS));
  const startMinutes = parseTime(value, START_KEYS);
  const endMinutes = parseTime(value, END_KEYS);

  const isReadable = days !== null && startMinutes !== null && endMinutes !== null && endMinutes > startMinutes;
  if (!isReadable) return null;
  return { id: `${index}-${name ?? ''}`, name, days, startMinutes, endMinutes };
}

// parseTime resolves the first matching time key and reads it as minutes since midnight.
function parseTime(record: Record<string, unknown>, keys: string[]): number | null {
  const key = findKey(record, keys);
  if (key === undefined) return null;
  const isMinutesKey = key.toLowerCase().endsWith('minutes');
  return parseMinutes(record[key], isMinutesKey);
}

// parseDays accepts weekday names or numbers and returns Monday-based indexes, or null.
function parseDays(value: unknown): number[] | null {
  const rawDays = Array.isArray(value) ? value : typeof value === 'string' ? value.split(/[,\s]+/) : null;
  if (!rawDays || rawDays.length === 0) return null;

  const numericDays = rawDays.filter((day): day is number => typeof day === 'number');
  const usesJsConvention = numericDays.includes(0);

  const days = rawDays.map((day) => {
    if (typeof day === 'number') return weekdayIndexFromNumber(day, usesJsConvention);
    if (typeof day === 'string') {
      const asNumber = Number(day);
      return Number.isNaN(asNumber) ? weekdayIndexFromName(day) : weekdayIndexFromNumber(asNumber, usesJsConvention);
    }
    return undefined;
  });

  const hasUnknownDay = days.some((day) => day === undefined);
  if (hasUnknownDay) return null;
  return Array.from(new Set(days as number[])).sort((a, b) => a - b);
}

// weekdayIndexFromNumber maps ISO (1=Mon..7=Sun) or JS (0=Sun..6=Sat) numbering to Monday-based.
function weekdayIndexFromNumber(day: number, usesJsConvention: boolean): number | undefined {
  if (!Number.isInteger(day)) return undefined;
  if (usesJsConvention) return day >= 0 && day <= 6 ? (day + 6) % 7 : undefined;
  return day >= 1 && day <= 7 ? day - 1 : undefined;
}

function weekdayIndexFromName(name: string): number | undefined {
  return WEEKDAY_ALIASES[name.trim().toLowerCase()];
}

// parseMinutes reads "HH:MM[:SS]", an hour count (<= 24) or minutes since midnight.
// isMinutes forces numeric input to be read as minutes (needed for small values like 0 or 15).
function parseMinutes(value: unknown, isMinutes = false): number | null {
  if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
    const minutes = !isMinutes && value <= 24 ? value * 60 : value;
    return minutes <= MINUTES_PER_DAY ? Math.round(minutes) : null;
  }
  if (typeof value !== 'string') return null;

  const match = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(value.trim());
  if (!match) return parseMinutes(Number(value), isMinutes);
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  const isValidClock = hours <= 24 && minutes < 60;
  return isValidClock ? hours * 60 + minutes : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// findKey returns the first matching key of the record, ignoring case.
function findKey(record: Record<string, unknown>, keys: string[]): string | undefined {
  const lowerKeys = keys.map((key) => key.toLowerCase());
  return Object.keys(record).find((key) => lowerKeys.includes(key.toLowerCase()));
}

function valueForKeys(record: Record<string, unknown>, keys: string[]): unknown {
  const key = findKey(record, keys);
  return key === undefined ? undefined : record[key];
}
